import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { IconLogin } from '@tabler/icons-react';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ApiClientError } from '@/types/api';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const { login, isLoggingIn } = useAuth();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormValues) {
    try {
      await login(values);
    } catch (error) {
      if (error instanceof ApiClientError) {
        setError('root', { message: error.message });
      }
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/vynzo-logo.svg" alt="Vynzo" className="h-12 w-12" />
          <h1 className="font-display text-2xl font-extrabold text-text-primary">Vynzo Admin</h1>
          <p className="text-sm text-text-secondary">Sign in to manage your streaming platform</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 rounded-2xl border border-border-subtle bg-bg-elevated p-8 shadow-xl">
          {errors.root && (
            <div className="rounded-md border border-state-error/30 bg-state-error-subtle px-4 py-3 text-sm text-state-error">
              {errors.root.message}
            </div>
          )}

          <Input
            label="Email"
            type="email"
            autoComplete="email"
            placeholder="you@vynzo.app"
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            error={errors.password?.message}
            {...register('password')}
          />

          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-xs font-medium text-text-link hover:underline">
              Forgot password?
            </Link>
          </div>

          <Button type="submit" loading={isLoggingIn} fullWidth>
            <IconLogin className="h-4 w-4" />
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
